import { Link } from "react-router-dom";
import { usePageData } from "@/hooks/usePageData";
import { useSEO } from "@/hooks/useSEO";
import { Globe, FileText, Scale, Users, ArrowRight, MapPin } from "lucide-react";

const iconMap = { Globe, FileText, Scale, Users, MapPin };
const getIcon = (name) => iconMap[name] || Globe;

export default function InternationalPage() {
  const { sections } = usePageData("international");
  const hero = sections.hero || {};
  const regions = sections.regions || {};
  const workTypes = sections.work_types || {};
  const approach = sections.approach || {};
  const cta = sections.cta || {};

  const defaultRegions = [
    { name: "India", image: "https://images.unsplash.com/photo-1768807842143-d1493bde6931?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2OTV8MHwxfHNlYXJjaHwzfHxtdW1iYWklMjBza3lsaW5lJTIwbWluaW1hbGlzdCUyMGFyY2hpdGVjdHVyZXxlbnwwfHx8fDE3NzIwMTg0ODN8MA&ixlib=rb-4.1.0&q=85", description: "Our home base. Corporate legal advisory across all major commercial centres." },
    { name: "Middle East", image: "https://images.pexels.com/photos/31033517/pexels-photo-31033517.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940", description: "Supporting Indian businesses in the UAE, Saudi Arabia, and other GCC markets." },
    { name: "United Kingdom", image: "https://images.unsplash.com/photo-1732635456274-ea67cdde1e0c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHw0fHxsb25kb24lMjBza3lsaW5lJTIwbWluaW1hbGlzdCUyMGFyY2hpdGVjdHVyZXxlbnwwfHx8fDE3NzIwMTg0ODV8MA&ixlib=rb-4.1.0&q=85", description: "Cross-border contracts, dispute strategy, and coordination with UK-based counsel." }
  ];

  const defaultWorkTypes = [
    { title: "Cross-Border Contracting", description: "Drafting and negotiating contracts with overseas counterparties.", icon: "FileText" },
    { title: "Joint Ventures & Partnerships", description: "Structuring joint ventures and agency relationships across legal systems.", icon: "Users" },
    { title: "International Disputes", description: "Strategic coordination of commercial disputes and arbitration proceedings.", icon: "Scale" },
    { title: "Regulatory Navigation", description: "Advising on cross-border regulatory requirements and compliance.", icon: "Globe" }
  ];

  const defaultApproachItems = [
    { num: "01", title: "Single Point of Contact", description: "One relationship, one team, coordinated advice." },
    { num: "02", title: "Partner Firm Network", description: "Carefully selected local counsel in each jurisdiction." },
    { num: "03", title: "Commercial Outcomes", description: "Emphasis on enforceability, practical results, and proportionate cost." }
  ];

  const pageTitle = hero.meta_title || "International | Equaliser Legal LLP";
  const pageDesc = hero.meta_description || "Cross-border advisory across India, the Middle East, and the UK. One point of contact for international commercial matters.";
  useSEO(pageTitle, pageDesc);

  return (
    <div data-testid="international-page">
      <section className="bg-[#0A1628] noise-overlay relative pt-28 pb-20 md:pt-36 md:pb-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <p className="text-[#C5A059] text-xs uppercase tracking-[0.25em] mb-4">{hero.subtitle || "International"}</p>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-white font-medium leading-tight tracking-tight max-w-4xl">{hero.title || "Cross-border advisory across India, the Middle East, and the UK."}</h1>
          <p className="text-white/50 text-base md:text-lg leading-relaxed max-w-2xl mt-6">{hero.body || ""}</p>
        </div>
      </section>

      <section data-testid="regions-section" className="bg-white py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <p className="text-[#C5A059] text-xs uppercase tracking-[0.25em] mb-4">{regions.subtitle || "Our Reach"}</p>
          <h2 className="font-heading text-3xl md:text-4xl text-[#0A1628] font-medium tracking-tight mb-16">{regions.title || "Three regions, one advisory."}</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {(regions.items || defaultRegions).map((region, i) => (
              <div key={i} data-testid={`region-card-${i}`} className="border border-slate-200 overflow-hidden hover:border-[#0A1628] transition-colors duration-300 group">
                <div className="aspect-[16/9] overflow-hidden">
                  <img src={region.image} alt={`${region.name || region.title} skyline`} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
                <div className="p-8">
                  <div className="flex items-center gap-2 mb-3"><MapPin size={14} className="text-[#C5A059]" /><h3 className="font-heading text-xl text-[#0A1628] font-medium">{region.name || region.title}</h3></div>
                  <p className="text-slate-500 text-sm leading-relaxed">{region.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section data-testid="work-types-section" className="bg-[#F8FAFC] py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <p className="text-[#C5A059] text-xs uppercase tracking-[0.25em] mb-4">{workTypes.subtitle || "Types of Cross-Border Work"}</p>
          <h2 className="font-heading text-3xl md:text-4xl text-[#0A1628] font-medium tracking-tight mb-16">{workTypes.title || "What we do across borders."}</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {(workTypes.items || defaultWorkTypes).map((item, i) => {
              const Icon = getIcon(item.icon);
              return (
                <div key={i} data-testid={`work-type-${i}`} className="bg-white border border-slate-200 p-8 hover:border-[#0A1628] transition-colors duration-300 group">
                  <Icon size={24} className="text-slate-400 group-hover:text-[#C5A059] transition-colors duration-300 mb-4" strokeWidth={1.5} />
                  <h3 className="font-heading text-lg text-[#0A1628] font-medium mb-3">{item.title}</h3>
                  <p className="text-slate-500 text-sm leading-relaxed">{item.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section data-testid="approach-section" className="bg-white py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            <div>
              <p className="text-[#C5A059] text-xs uppercase tracking-[0.25em] mb-4">{approach.subtitle || "Our Approach"}</p>
              <h2 className="font-heading text-3xl md:text-4xl text-[#0A1628] font-medium tracking-tight mb-8">{approach.title || "One point of contact. Coordinated advice."}</h2>
              <div className="text-slate-600 text-base leading-relaxed space-y-5" dangerouslySetInnerHTML={{ __html: approach.body || "<p>When your business operates across borders, Equaliser Legal LLP serves as your single point of contact.</p>" }} />
            </div>
            <div className="space-y-6">
              {(approach.items || defaultApproachItems).map((item, i) => (
                <div key={i} className="border border-slate-200 p-6 hover:border-[#0A1628] transition-colors duration-300">
                  <span className="text-[#C5A059] text-xs tracking-widest">{item.num}</span>
                  <h3 className="font-heading text-lg text-[#0A1628] font-medium mt-2 mb-2">{item.title}</h3>
                  <p className="text-slate-500 text-sm leading-relaxed">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="bg-[#0A1628] noise-overlay relative py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-heading text-3xl md:text-4xl text-white font-medium tracking-tight mb-6">{cta.title || "Have a cross-border matter?"}</h2>
          <p className="text-white/50 text-base mb-10 max-w-xl mx-auto">{cta.body || ""}</p>
          <Link to={cta.cta_link || "/contact"} data-testid="international-cta" className="inline-flex items-center justify-center bg-[#C5A059] text-[#0A1628] px-10 py-4 text-sm uppercase tracking-[0.15em] font-medium hover:bg-[#d4af6a] transition-colors duration-300 rounded-[2px]">{cta.cta_text || "Talk to us"} <ArrowRight size={16} className="ml-2" /></Link>
        </div>
      </section>
    </div>
  );
}
